// Golf Match Play Tracker 2026 -- Course handicaps & stroke allocation
//
// =====================================================================
// WHY THIS FILE EXISTS
// =====================================================================
// Players choose their own tee box, and a match can have two players on
// two different tees. Comparing raw Handicap Indexes would be unfair --
// the player on the longer tee is facing a harder golf course.
//
// The World Handicap System solves this inside the Course Handicap
// formula. For a NINE HOLE match:
//
//   Course Handicap (9) = (Handicap Index / 2) x (9-hole Slope / 113)
//                         + (9-hole Course Rating - 9-hole Par)
//
// The (Course Rating - Par) term is the tee-box rating adjustment. It is
// what makes a White-tee player and a Gray-tee player directly
// comparable, so strokes given are simply the difference between the two
// players' Course Handicaps.
//
// LEAGUE RULES LAYERED ON TOP OF WHS
//
//   1. HANDICAP CAP -- a player's Handicap Index is capped at 18.0 for
//      stroke purposes. A 24.3 index plays as an 18.0. The player's true
//      index is still stored and displayed; the cap only affects the
//      stroke calculation. There is no cap on plus (negative) indexes.
//
//   2. 85% ALLOWANCE -- each side's 9-hole Course Handicap is multiplied
//      by 0.85 to get their PLAYING HANDICAP. This compresses the spread
//      so the high handicapper does not get the full difference.
//
//   3. STROKE CEILING -- no player receives more than 8 strokes in a
//      match. Only 8 of the 9 holes are stroke holes (see below), so 8
//      strokes is one per stroke hole. Anything beyond that is clipped.
//
// Order of operations (this order matters):
//      cap index at 18  ->  9-hole Course Handicap  ->  x 0.85
//      ->  round each side  ->  difference  ->  clip to 8 = strokes given
//
// Strokes are then played off the low man: the lower Playing Handicap
// plays scratch, the higher receives the difference.
//
// NOTE ON HOLE 9 -- by league vote, hole 9 is NOT a stroke hole. The
// eight remaining holes shift up one rating (the hole rated No. 2
// becomes No. 1, No. 3 becomes No. 2, and so on) so a match is never
// decided on the last hole by a handicap stroke. The app does not track
// hole-by-hole play, so this is enforced on the card by the players --
// it is documented on the League Rules tab. It is also why the ceiling
// above is 8 rather than 9.
// =====================================================================

const STANDARD_SLOPE = 113;

// League rules -- change these in one place.
export const MAX_HANDICAP_INDEX = 18;
export const MATCH_HANDICAP_ALLOWANCE = 0.85;
// Hole 9 is not a stroke hole, so there are only 8 stroke holes.
export const MAX_STROKES_PER_MATCH = 8;
export const STROKE_HOLES = 8;

// Pull the rating/slope/par for one nine out of a tee_sets row.
export function teeNineData(teeSet, nine) {
  if (!teeSet) return null;
  if (nine === "B9") {
    return { rating: num(teeSet.b9_rating), slope: num(teeSet.b9_slope), par: num(teeSet.b9_par) };
  }
  return { rating: num(teeSet.f9_rating), slope: num(teeSet.f9_slope), par: num(teeSet.f9_par) };
}

function num(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function teeSetHasNineData(teeSet, nine) {
  const d = teeNineData(teeSet, nine);
  return !!(d && d.rating !== null && d.slope !== null && d.par !== null);
}

/**
 * Cap a Handicap Index at the league maximum. Plus handicaps pass
 * through untouched -- the cap is a ceiling, not a floor.
 */
export function cappedIndex(handicapIndex) {
  const hi = num(handicapIndex);
  if (hi === null) return null;
  return Math.min(hi, MAX_HANDICAP_INDEX);
}

export function isIndexCapped(handicapIndex) {
  const hi = num(handicapIndex);
  return hi !== null && hi > MAX_HANDICAP_INDEX;
}

/**
 * 9-hole Course Handicap, plus the league's 85% Playing Handicap.
 *
 * The index is capped at MAX_HANDICAP_INDEX before the WHS formula runs.
 *
 * @param handicapIndex signed index (negative = plus handicap)
 * @param teeSet        a tee_sets row
 * @param nine          "F9" | "B9"
 * @returns {
 *   exact,        // 9-hole Course Handicap, unrounded (capped index)
 *   rounded,      // 9-hole Course Handicap, rounded
 *   playingExact, // exact x 0.85, unrounded
 *   playing,      // exact x 0.85, rounded -- THIS drives strokes given
 *   indexUsed,    // the index actually used (post-cap)
 *   indexCapped,  // true if the cap changed the number
 * } or null if the tee set is missing data
 */
export function courseHandicap9(handicapIndex, teeSet, nine) {
  const raw = num(handicapIndex);
  if (raw === null) return null;
  const hi = Math.min(raw, MAX_HANDICAP_INDEX);
  const d = teeNineData(teeSet, nine);
  if (!d || d.rating === null || d.slope === null || d.par === null) return null;

  const exact = (hi / 2) * (d.slope / STANDARD_SLOPE) + (d.rating - d.par);
  const playingExact = exact * MATCH_HANDICAP_ALLOWANCE;
  return {
    exact,
    rounded: roundHalfUp(exact),
    playingExact,
    playing: roundHalfUp(playingExact),
    indexUsed: hi,
    indexCapped: raw > MAX_HANDICAP_INDEX,
  };
}

// Golf rounds .5 away from zero (-2.5 -> -3, 2.5 -> 3).
function roundHalfUp(n) {
  return n < 0 ? -Math.round(Math.abs(n)) : Math.round(n);
}

/**
 * Strokes for one head-to-head match.
 *
 * Each side is: { golfer, teeSet }
 *
 * Returns:
 *  {
 *    aGets, bGets,           // whole strokes received
 *    aCourseHandicap,        // rounded 9-hole CH at 100% (null if unavailable)
 *    bCourseHandicap,
 *    aPlayingHandicap,       // rounded CH x 85% -- what strokes are based on
 *    bPlayingHandicap,
 *    aIndexCapped,           // true if their index was clipped to 18
 *    bIndexCapped,
 *    rawStrokes,             // the difference BEFORE the 8-stroke ceiling
 *    strokesClipped,         // true if the ceiling reduced the strokes given
 *    method,                 // 'course_handicap' | 'index_estimate' | 'unavailable'
 *    crossTee,               // true if the two players are on different tees
 *    note,                   // short human-readable summary
 *    notice,                 // informational (e.g. the 18 cap applied) -- NOT a problem
 *    warning                 // set when falling back or data is missing -- needs fixing
 *  }
 */
export function strokesForMatch(a, b, nine = "F9") {
  const empty = {
    aGets: 0, bGets: 0,
    aCourseHandicap: null, bCourseHandicap: null,
    aPlayingHandicap: null, bPlayingHandicap: null,
    aIndexCapped: false, bIndexCapped: false,
    rawStrokes: 0, strokesClipped: false,
    method: "unavailable", crossTee: false,
    note: "--", notice: null, warning: "Handicap index missing",
  };
  if (!a?.golfer || !b?.golfer) return empty;

  const aRaw = num(a.golfer.handicap_index);
  const bRaw = num(b.golfer.handicap_index);
  if (aRaw === null || bRaw === null) return empty;

  // League rule 1: cap the index at 18 before anything else.
  const aHI = Math.min(aRaw, MAX_HANDICAP_INDEX);
  const bHI = Math.min(bRaw, MAX_HANDICAP_INDEX);
  const aIndexCapped = aRaw > MAX_HANDICAP_INDEX;
  const bIndexCapped = bRaw > MAX_HANDICAP_INDEX;
  // The cap firing is normal league behaviour, not a data problem, so it
  // goes in `notice` rather than `warning`.
  const capped = [
    aIndexCapped ? a.golfer.name : null,
    bIndexCapped ? b.golfer.name : null,
  ].filter(Boolean);
  const notice = capped.length
    ? `Index capped at ${MAX_HANDICAP_INDEX} for ${capped.join(" and ")}`
    : null;

  const crossTee = !!(a.teeSet && b.teeSet && a.teeSet.id !== b.teeSet.id);

  const aCH = courseHandicap9(aHI, a.teeSet, nine);
  const bCH = courseHandicap9(bHI, b.teeSet, nine);

  // --- Preferred path: full WHS course handicaps (includes the tee-box
  //     rating adjustment, so cross-tee matches are exact), then league
  //     rule 2 -- the 85% allowance on each side. ---
  if (aCH && bCH) {
    const diff = aCH.playing - bCH.playing;
    // League rule 3: clip to the 8-stroke ceiling.
    const rawStrokes = Math.abs(diff);
    const strokes = Math.min(rawStrokes, MAX_STROKES_PER_MATCH);
    const strokesClipped = rawStrokes > MAX_STROKES_PER_MATCH;
    return {
      aGets: diff > 0 ? strokes : 0,
      bGets: diff < 0 ? strokes : 0,
      rawStrokes, strokesClipped,
      aCourseHandicap: aCH.rounded,
      bCourseHandicap: bCH.rounded,
      aPlayingHandicap: aCH.playing,
      bPlayingHandicap: bCH.playing,
      aExact: aCH.exact,
      bExact: bCH.exact,
      aPlayingExact: aCH.playingExact,
      bPlayingExact: bCH.playingExact,
      aIndexCapped, bIndexCapped,
      allowance: MATCH_HANDICAP_ALLOWANCE,
      method: "course_handicap",
      crossTee,
      note: strokes === 0
        ? "Even"
        : `${strokes} stroke${strokes === 1 ? "" : "s"} to ${diff > 0 ? a.golfer.name : b.golfer.name}`,
      notice: [
        notice,
        strokesClipped
          ? `Capped at the ${MAX_STROKES_PER_MATCH}-stroke match maximum (would have been ${rawStrokes})`
          : null,
      ].filter(Boolean).join(" · ") || null,
      warning: null,
    };
  }

  // --- Fallback: no rating/slope entered for one or both tee sets. Use
  //     half the index difference (9 holes) at 85%. This IGNORES the
  //     tee-box rating adjustment, so it is only an estimate. ---
  const diff = ((aHI - bHI) / 2) * MATCH_HANDICAP_ALLOWANCE;
  const rawStrokes = Math.abs(roundHalfUp(diff));
  const strokes = Math.min(rawStrokes, MAX_STROKES_PER_MATCH);
  const strokesClipped = rawStrokes > MAX_STROKES_PER_MATCH;
  const missing = [];
  if (!aCH) missing.push(a.teeSet?.name ? `${a.teeSet.name} tees` : `${a.golfer.name}'s tees`);
  if (!bCH) missing.push(b.teeSet?.name ? `${b.teeSet.name} tees` : `${b.golfer.name}'s tees`);

  return {
    aGets: diff > 0 ? strokes : 0,
    bGets: diff < 0 ? strokes : 0,
    aCourseHandicap: aCH ? aCH.rounded : null,
    bCourseHandicap: bCH ? bCH.rounded : null,
    aPlayingHandicap: aCH ? aCH.playing : null,
    bPlayingHandicap: bCH ? bCH.playing : null,
    aIndexCapped, bIndexCapped,
    rawStrokes, strokesClipped,
    allowance: MATCH_HANDICAP_ALLOWANCE,
    method: "index_estimate",
    crossTee,
    note: strokes === 0
      ? "Even (est.)"
      : `${strokes} stroke${strokes === 1 ? "" : "s"} to ${diff > 0 ? a.golfer.name : b.golfer.name} (est.)`,
    notice: [
      notice,
      strokesClipped
        ? `Capped at the ${MAX_STROKES_PER_MATCH}-stroke match maximum (would have been ${rawStrokes})`
        : null,
    ].filter(Boolean).join(" · ") || null,
    warning: `Estimated from handicap index at ${Math.round(MATCH_HANDICAP_ALLOWANCE * 100)}% -- add rating/slope for ${missing.join(" and ")} on the Courses tab`
      + (crossTee ? " (cross-tee match, adjustment not applied)" : ""),
  };
}

// Resolve which tee set a golfer plays in a given match.
//
// Resolution order:
//   1. A per-match override (they moved up or back for this one round).
//   2. Their tee TIER, mapped to this course's tee of that tier. A tier
//      spans courses, so "White / Black" gives White at Champions Pointe
//      and Black at Covered Bridge automatically.
//   3. A legacy single-course default tee, if one is still set.
//   4. The course's first tee, as a last resort.
export function resolveTeeSet(match, golferId, golfer, teeSets) {
  const sel = match?.golfer_selections?.[golferId];
  const courseTees = (teeSets || []).filter(t => t.course_id === match?.course_id);

  // 1. Per-match override
  if (sel?.tee_set_id) {
    const found = courseTees.find(t => t.id === sel.tee_set_id) ||
                  (teeSets || []).find(t => t.id === sel.tee_set_id);
    if (found) return found;
  }

  // 2. Tee tier -> this course's tee at that tier
  const tier = golfer?.default_tee_tier;
  if (tier !== null && tier !== undefined && tier !== '') {
    const found = courseTees.find(t => Number(t.tee_tier) === Number(tier));
    if (found) return found;
  }

  // 3. Legacy per-course default
  if (golfer?.default_tee_set_id) {
    const found = courseTees.find(t => t.id === golfer.default_tee_set_id);
    if (found) return found;
  }

  // 4. Fallback
  return courseTees
    .slice()
    .sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0))[0] || null;
}
